/**
 * Sticky ATC pill dynamic background color.
 *
 * Extracts the dominant (most saturated) color from the first gallery
 * image via a downscaled canvas pass and applies it as CSS variables
 * `--sticky-pill-bg` / `--sticky-pill-fg` on the sticky form card.
 * Updates on variant change.
 *
 * Perf notes:
 *   - Results are cached per image URL (Map) so variant toggling is free.
 *   - Canvas + typed arrays are reused across extractions.
 *   - RGB→HSL is inlined in the pixel loop (no per-pixel function call).
 *   - Extraction is scheduled on requestIdleCallback so it never blocks
 *     first paint or interactive frames.
 *   - Custom properties are registered via @property so their transition
 *     is a true color interpolation instead of a step change.
 */
(function () {
  'use strict';

  const SAMPLE_SIZE = 48;          // downscale target (was 64)
  const CENTER_FRACTION = 0.4;     // sample the central 40% × 40%
  const SAT_MIN = 0.12;
  const LIGHT_MIN = 0.08;
  const LIGHT_MAX = 0.92;
  const BIN_COUNT = 36;
  const LIGHT_TEXT_LUM = 0.55;     // above → dark text, below → white text
  const TRANSITION_MS = 500;

  // ---------------------------------------------------------------------
  // Smooth CSS variable interpolation.
  // Without @property, changing --sticky-pill-bg is a discrete step and
  // the transition on `background-color` interpolates anyway — but once
  // the browser supports @property it gives us a cleaner animation
  // (especially when multiple dependent properties read the same var).
  // ---------------------------------------------------------------------
  if (typeof CSS !== 'undefined' && CSS.registerProperty) {
    try {
      CSS.registerProperty({
        name: '--sticky-pill-bg',
        syntax: '<color>',
        inherits: true,
        initialValue: 'transparent',
      });
      CSS.registerProperty({
        name: '--sticky-pill-fg',
        syntax: '<color>',
        inherits: true,
        initialValue: 'currentColor',
      });
    } catch (_) { /* already registered — ignore */ }
  }

  // ---------------------------------------------------------------------
  // Extraction plumbing.
  // ---------------------------------------------------------------------
  const colorCache = new Map();    // url → [r,g,b] | null
  const inFlight = new Map();      // url → Promise<[r,g,b]|null>

  // Pre-allocated bin accumulators — reused across every extraction.
  const rSum = new Float32Array(BIN_COUNT);
  const gSum = new Float32Array(BIN_COUNT);
  const bSum = new Float32Array(BIN_COUNT);
  const wSum = new Float32Array(BIN_COUNT);

  // Reused canvas + context.
  let _canvas = null;
  let _ctx = null;
  function getContext(w, h) {
    if (!_canvas) {
      _canvas = typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(w, h)
        : document.createElement('canvas');
    }
    _canvas.width = w;
    _canvas.height = h;
    if (!_ctx) {
      _ctx = _canvas.getContext('2d', { willReadFrequently: true });
    }
    return _ctx;
  }

  function idle() {
    return new Promise((resolve) => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => resolve(), { timeout: 250 });
      } else {
        setTimeout(resolve, 0);
      }
    });
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });
  }

  function extractFromImage(img) {
    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) return null;

    const cropW = (srcW * CENTER_FRACTION) | 0;
    const cropH = (srcH * CENTER_FRACTION) | 0;
    const cropX = ((srcW - cropW) * 0.5) | 0;
    const cropY = ((srcH - cropH) * 0.5) | 0;

    const w = SAMPLE_SIZE;
    const h = Math.max(1, Math.round((cropH / cropW) * SAMPLE_SIZE));

    const ctx = getContext(w, h);
    ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, w, h);

    let data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (_) {
      // Tainted canvas — CORS failed.
      return null;
    }

    // Reset bins (typed arrays).
    rSum.fill(0); gSum.fill(0); bSum.fill(0); wSum.fill(0);

    const INV_255 = 1 / 255;
    const len = data.length;

    for (let i = 0; i < len; i += 4) {
      const a = data[i + 3];
      if (a < 200) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      // Inlined RGB→HSL (hot path).
      const rn = r * INV_255;
      const gn = g * INV_255;
      const bn = b * INV_255;

      const max = rn > gn ? (rn > bn ? rn : bn) : (gn > bn ? gn : bn);
      const min = rn < gn ? (rn < bn ? rn : bn) : (gn < bn ? gn : bn);
      const l = (max + min) * 0.5;
      if (l < LIGHT_MIN || l > LIGHT_MAX) continue;

      const d = max - min;
      if (d === 0) continue; // achromatic, skip
      const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (s < SAT_MIN) continue;

      let hue;
      if (max === rn)      hue = (gn - bn) / d + (gn < bn ? 6 : 0);
      else if (max === gn) hue = (bn - rn) / d + 2;
      else                 hue = (rn - gn) / d + 4;
      hue *= 1 / 6;

      let bin = (hue * BIN_COUNT) | 0;
      if (bin >= BIN_COUNT) bin = BIN_COUNT - 1;

      const weight = s * (1 - Math.abs(0.5 - l) * 1.4);
      if (weight <= 0) continue;

      rSum[bin] += r * weight;
      gSum[bin] += g * weight;
      bSum[bin] += b * weight;
      wSum[bin] += weight;
    }

    let bestBin = -1;
    let bestWeight = 0;
    for (let i = 0; i < BIN_COUNT; i++) {
      if (wSum[i] > bestWeight) { bestWeight = wSum[i]; bestBin = i; }
    }
    if (bestBin < 0) return null;

    const inv = 1 / wSum[bestBin];
    return [
      Math.round(rSum[bestBin] * inv),
      Math.round(gSum[bestBin] * inv),
      Math.round(bSum[bestBin] * inv),
    ];
  }

  function extractDominant(src) {
    if (!src) return Promise.resolve(null);
    if (colorCache.has(src)) return Promise.resolve(colorCache.get(src));
    const pending = inFlight.get(src);
    if (pending) return pending;

    const p = (async () => {
      try {
        const img = await loadImage(src);
        if (img.decode) { try { await img.decode(); } catch (_) {} }
        await idle();
        const rgb = extractFromImage(img);
        colorCache.set(src, rgb);
        return rgb;
      } catch (_) {
        colorCache.set(src, null);
        return null;
      } finally {
        inFlight.delete(src);
      }
    })();
    inFlight.set(src, p);
    return p;
  }

  // ---------------------------------------------------------------------
  // Applying the color.
  // ---------------------------------------------------------------------
  function relativeLuminance(r, g, b) {
    const f = (v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  }

  function applyColor(card, rgb) {
    if (!card) return;
    if (rgb) {
      const [r, g, b] = rgb;
      const useDarkText = relativeLuminance(r, g, b) > LIGHT_TEXT_LUM;
      card.style.setProperty('--sticky-pill-bg', `rgb(${r}, ${g}, ${b})`);
      card.style.setProperty('--sticky-pill-fg', useDarkText ? '#111' : '#fff');
      card.classList.add('has-dynamic-bg');
      card.classList.toggle('has-dynamic-bg--light', useDarkText);
    } else {
      card.style.removeProperty('--sticky-pill-bg');
      card.style.removeProperty('--sticky-pill-fg');
      card.classList.remove('has-dynamic-bg', 'has-dynamic-bg--light');
    }
  }

  // Always read from the FIRST gallery image (variant's featured media),
  // never the currently-browsed slide.
  function findSourceSrc(stickyForm) {
    const galleryImg = document.querySelector('media-gallery img, .product__gallery img');
    const img = (galleryImg && (galleryImg.currentSrc || galleryImg.src))
      ? galleryImg
      : stickyForm.querySelector('[data-sticky-product-media] img');
    if (!img) return null;
    return img.currentSrc || img.src || null;
  }

  // ---------------------------------------------------------------------
  // Wire-up per <product-sticky-form>.
  // ---------------------------------------------------------------------
  function init(stickyForm) {
    if (stickyForm.__pillInit) return;
    stickyForm.__pillInit = true;

    const card = stickyForm.querySelector('.product-sticky-form__card');
    if (!card) return;

    let currentSrc = null;
    let token = 0;

    const refresh = () => {
      const src = findSourceSrc(stickyForm);
      if (!src || src === currentSrc) return;
      currentSrc = src;
      const my = ++token;
      extractDominant(src).then((rgb) => {
        if (my !== token) return; // superseded by a newer variant change
        applyColor(card, rgb);
      });
    };

    // Initial pass. One late retry covers the case where the gallery
    // image mounts after the sticky form (lazy/hydration).
    refresh();
    setTimeout(refresh, 300);

    // Variant change — wait two frames so the DOM has the new src.
    const onVariant = () => {
      requestAnimationFrame(() => requestAnimationFrame(refresh));
    };
    document.addEventListener('variant:change', onVariant);

    // Manual hook for debugging.
    stickyForm.__pillRefresh = refresh;
  }

  function boot() {
    const stickies = document.querySelectorAll('product-sticky-form');
    if (!stickies.length) return;
    stickies.forEach(init);
    window.__stickyPillColor = {
      refresh: () => stickies.forEach((s) => s.__pillRefresh && s.__pillRefresh()),
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
